import { useRouter, usePathname, useSearchParams, useParams } from 'next/navigation';
import { Chip, useI18n } from '@packages/shared';
import { useIsBot } from '@packages/tracking';
import { getFragmentData, type FragmentType } from '@packages/gql/generated/shopping';
import { RatingTagFragmentFragmentDoc } from '@packages/gql/generated/shopping/RatingTagFragmentFragmentDoc';
import { RatingTagSkuFragmentFragmentDoc } from '@packages/gql/generated/shopping/RatingTagSkuFragmentFragmentDoc';
import { ratingDetailPagePath, ratingFilterTagPrefix } from '../config';
import { useRatingTracking } from '../../Tracking/hooks/useRatingTracking';
import type { CustomerRatingsParams } from '../types';
import { getSearchParamsUrlSuffix } from '../helper/getSearchParamsUrlSuffix';

/* GraphQL */ `
  fragment RatingTagFragment on RatingTag {
    ratingCount
    tag {
      id
      key
      name
    }
  }
`;

/* GraphQL */ `
  fragment RatingTagSkuFragment on Product {
    sku
  }
`;

export type RatingTagProps = {
  tagData: FragmentType<typeof RatingTagFragmentFragmentDoc>;
  /**
   * sku needed for product in rating page
   */
  skuData: FragmentType<typeof RatingTagSkuFragmentFragmentDoc>;
  /**
   * category for glycerin tracking
   */
  category: 'Vorteile' | 'Nachteile';
};

export const RatingTag = ({ tagData, skuData, category }: RatingTagProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams<CustomerRatingsParams>();
  const { push } = useRouter();
  const isBot = useIsBot();
  const { language, localizeUrl } = useI18n();

  const { trackGlycerinButtonClick } = useRatingTracking();

  if (!tagData) return null;

  const {
    ratingCount,
    tag: { id, key, name },
  } = getFragmentData(RatingTagFragmentFragmentDoc, tagData);
  const { sku } = getFragmentData(RatingTagSkuFragmentFragmentDoc, skuData);

  const filterTag = `${ratingFilterTagPrefix}${key}`;
  const isActive = params?.ftag ? params.ftag[0] === filterTag : false;

  const onClickHandler = () => {
    if (key && !isBot && pathname !== null && params !== null) {
      trackGlycerinButtonClick({
        category,
        detail: name,
      });
      push(
        localizeUrl(
          `/p/${params.slug}/${params.akl}/rating${isActive ? '' : `/${filterTag}`}${getSearchParamsUrlSuffix(searchParams, sku)}`,
          language,
        ),
        { scroll: !pathname.includes(ratingDetailPagePath) },
      );
    }
  };

  return (
    <Chip
      key={id}
      component="li"
      label={`${name} (${ratingCount})`}
      color={isActive ? 'primary' : 'default'}
      onClick={onClickHandler}
    />
  );
};
